import { useMemo } from 'react';
import { CalendarDays, Clock, CheckCircle2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import {ETFDividend, ETFHolding} from "@/integrations/supabase/etf.models.ts";

export interface ETFDividendWithTicker extends ETFDividend {
  ticker_symbol: string;
}

interface ETFDividendTimelineProps {
  dividends: ETFDividendWithTicker[];
  isLoading?: boolean;
}

/** Returns today's date as a YYYY-MM-DD string (local time). */
export function getTodayString(): string {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Splits dividends into upcoming (>= today) and paid (< today) groups. */
export function groupDividends(
  dividends: ETFDividendWithTicker[],
  today: string
): { upcoming: ETFDividendWithTicker[]; paid: ETFDividendWithTicker[] } {
  const upcoming: ETFDividendWithTicker[] = [];
  const paid: ETFDividendWithTicker[] = [];

  for (const d of dividends) {
    if (d.payment_date >= today) {
      upcoming.push(d);
    } else {
      paid.push(d);
    }
  }

  // Upcoming: ascending (nearest first)
  upcoming.sort((a, b) => a.payment_date.localeCompare(b.payment_date));
  // Paid: descending (most recent first)
  paid.sort((a, b) => b.payment_date.localeCompare(a.payment_date));

  return { upcoming, paid };
}

/** Merges holdings into dividends to attach ticker_symbol. */
export function enrichDividendsWithTicker(
  dividends: ETFDividend[],
  holdings: ETFHolding[]
): ETFDividendWithTicker[] {
  const holdingMap = new Map(holdings.map((h) => [h.id, h.ticker_symbol]));
  return dividends.map((d) => ({
    ...d,
    ticker_symbol: holdingMap.get(d.etf_holding_id) ?? '—',
  }));
}

function formatDate(dateStr: string): string {
  // Parse as local date to avoid timezone shifts
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function formatCurrency(amount: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  }).format(amount);
}

interface DividendEntryProps {
  dividend: ETFDividendWithTicker;
  isLast: boolean;
  variant: 'upcoming' | 'paid';
}

function DividendEntry({ dividend, isLast, variant }: DividendEntryProps) {
  const dotColor = variant === 'upcoming' ? 'bg-green-500' : 'bg-muted-foreground';
  const lineColor = variant === 'upcoming' ? 'bg-green-500' : 'bg-border';

  return (
    <li
      className="flex items-start gap-3 relative"
      aria-label={`${dividend.ticker_symbol} dividend on ${formatDate(dividend.payment_date)}`}
    >
      {/* Timeline dot + connector */}
      <div className="flex flex-col items-center" aria-hidden="true">
        <div className={cn('w-3 h-3 rounded-full mt-1 shrink-0', dotColor)} />
        {!isLast && <div className={cn('w-0.5 flex-1 min-h-[2rem]', lineColor)} />}
      </div>

      {/* Content */}
      <div className="flex items-start justify-between flex-1 pb-4 gap-2">
        <div className="min-w-0">
          <p className="text-sm font-semibold" data-testid="ticker-symbol">
            {dividend.ticker_symbol}
          </p>
          <p className="text-xs text-muted-foreground" data-testid="payment-date">
            {formatDate(dividend.payment_date)}
          </p>
          <p className="text-xs text-muted-foreground" data-testid="amount-per-unit">
            {formatCurrency(dividend.amount_per_unit, dividend.currency)} / unit
          </p>
        </div>
        <div className="text-right shrink-0">
          <p className="text-sm font-bold" data-testid="total-amount">
            {formatCurrency(dividend.total_amount, dividend.currency)}
          </p>
          {variant === 'upcoming' ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
              <Clock className="h-3 w-3" aria-hidden="true" />
              Upcoming
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
              <CheckCircle2 className="h-3 w-3" aria-hidden="true" />
              Paid
            </span>
          )}
        </div>
      </div>
    </li>
  );
}

interface DividendGroupProps {
  title: string;
  icon: React.ReactNode;
  dividends: ETFDividendWithTicker[];
  variant: 'upcoming' | 'paid';
  emptyMessage: string;
}

function DividendGroup({ title, icon, dividends, variant, emptyMessage }: DividendGroupProps) {
  return (
    <section aria-label={title}>
      <div className="flex items-center gap-2 mb-3">
        {icon}
        <h3 className="text-sm font-semibold">{title}</h3>
        <span className="ml-auto text-xs text-muted-foreground">{dividends.length} record{dividends.length !== 1 ? 's' : ''}</span>
      </div>
      {dividends.length === 0 ? (
        <p className="text-sm text-muted-foreground py-3 text-center" data-testid={`empty-${variant}`}>
          {emptyMessage}
        </p>
      ) : (
        <ol aria-label={`${title} dividend entries`} className="space-y-0">
          {dividends.map((d, i) => (
            <DividendEntry
              key={d.id}
              dividend={d}
              isLast={i === dividends.length - 1}
              variant={variant}
            />
          ))}
        </ol>
      )}
    </section>
  );
}

export function ETFDividendTimeline({ dividends, isLoading = false }: ETFDividendTimelineProps) {
  const today = getTodayString();

  const { upcoming, paid } = useMemo(
    () => groupDividends(dividends, today),
    [dividends, today]
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base font-semibold flex items-center gap-2">
          <CalendarDays className="h-4 w-4" aria-hidden="true" />
          Dividend Timeline
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div aria-busy="true" className="space-y-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-12 bg-muted animate-pulse rounded" />
            ))}
          </div>
        ) : (
          <div className="space-y-6">
            <DividendGroup
              title="Upcoming"
              icon={<Clock className="h-4 w-4 text-green-500" aria-hidden="true" />}
              dividends={upcoming}
              variant="upcoming"
              emptyMessage="No upcoming dividend payments."
            />
            <div className="border-t" />
            <DividendGroup
              title="Paid"
              icon={<CheckCircle2 className="h-4 w-4 text-muted-foreground" aria-hidden="true" />}
              dividends={paid}
              variant="paid"
              emptyMessage="No paid dividends recorded."
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
